import { useEffect, useRef, useState } from "react";

import { dragHasFiles, readDrop, type DroppedFiles } from "./dropFiles";

/**
 * Files can be dropped anywhere on the page while an upload step is open. `dragging` is true from the
 * first `dragenter` carrying files until the drop or the drag leaves the window; a counter (not
 * `dragleave` on each element) keeps it from flickering as the pointer crosses children.
 */
export function useGlobalDrop(enabled: boolean, extensions: string[], onDrop: (dropped: DroppedFiles) => void) {
  const [dragging, setDragging] = useState(false);
  const depth = useRef(0);
  const latest = useRef({ extensions, onDrop });
  latest.current = { extensions, onDrop };

  useEffect(() => {
    if (!enabled) {
      depth.current = 0;
      setDragging(false);
      return;
    }
    const enter = (event: DragEvent) => {
      if (!dragHasFiles(event)) return;
      event.preventDefault();
      depth.current += 1;
      setDragging(true);
    };
    const over = (event: DragEvent) => {
      if (!dragHasFiles(event)) return;
      // without this the browser opens the file instead of handing it over
      event.preventDefault();
      if (event.dataTransfer) event.dataTransfer.dropEffect = "copy";
    };
    const leave = (event: DragEvent) => {
      if (!dragHasFiles(event)) return;
      depth.current = Math.max(0, depth.current - 1);
      if (depth.current === 0) setDragging(false);
    };
    const drop = (event: DragEvent) => {
      if (!dragHasFiles(event)) return;
      event.preventDefault();
      depth.current = 0;
      setDragging(false);
      // the entries must be read before anything is awaited
      void readDrop(event.dataTransfer, latest.current.extensions).then((dropped) => {
        if (dropped.files.length || dropped.skipped) latest.current.onDrop(dropped);
      });
    };
    window.addEventListener("dragenter", enter);
    window.addEventListener("dragover", over);
    window.addEventListener("dragleave", leave);
    window.addEventListener("drop", drop);
    return () => {
      window.removeEventListener("dragenter", enter);
      window.removeEventListener("dragover", over);
      window.removeEventListener("dragleave", leave);
      window.removeEventListener("drop", drop);
      depth.current = 0;
      setDragging(false);
    };
  }, [enabled]);

  return dragging;
}
